/** @format */

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PrintIcon from "@mui/icons-material/Print";
import FilterListIcon from "@mui/icons-material/FilterList";
import { HEADERS } from "../../../../API/UrlAndPaths";

/**
 * Payslip List Component
 * Created: October 30, 2025
 * Purpose: View and manage generated payslips
 */
const PayslipList = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    fromDate: null,
    toDate: null,
    status: "",
  });
  const [employeeImages, setEmployeeImages] = useState({});
  const printRef = useRef();

  // Print functions
  const handlePrintList = () => {
    const printWindow = window.open('', '_blank');
    const printStyles = `
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .header h2 { margin-bottom: 10px; }
        .header p { color: #666; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f5f5f5; font-weight: bold; }
        td img { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .status-chip { padding: 4px 8px; border-radius: 12px; font-size: 12px; font-weight: bold; }
        .status-posted { background-color: #e8f5e8; color: #2e7d32; }
        .status-draft { background-color: #fff3e0; color: #f57c00; }
        .status-done { background-color: #e3f2fd; color: #1976d2; }
        @media print { body { margin: 10px; } }
      </style>
    `;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payslip List</title>
          ${printStyles}
        </head>
        <body>
          <div class="header">
            <h2>PAYSLIP LIST</h2>
            <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
            <p>Period: ${filters.fromDate ? formatDate(filters.fromDate) : 'All'} - ${filters.toDate ? formatDate(filters.toDate) : 'All'}</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Payslip No.</th>
                <th>Employee</th>
                <th>Period</th>
                <th>Work Days</th>
                <th class="text-right">Gross Salary</th>
                <th class="text-right">Deductions</th>
                <th class="text-right">Net Salary</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${payslips.map(payslip => `
                <tr>
                  <td class="text-center">
                    ${payslip.employeeImage ? 
                      `<img src="${payslip.employeeImage}" alt="${payslip.employeeDetails?.staff_name || 'Employee'}" onerror="this.style.display='none'">` : 
                      '<div style="width:40px;height:40px;background:#f0f0f0;border-radius:50%;margin:0 auto;"></div>'
                    }
                  </td>
                  <td>${payslip.name}</td>
                  <td>
                    <div>${payslip.employeeDetails?.staff_name || 'N/A'}</div>
                    <small style="color:#666">${payslip.employeeDetails?.emp_id || ''}</small>
                  </td>
                  <td>${formatDate(payslip.fromDate)} - ${formatDate(payslip.toDate)}</td>
                  <td class="text-center">${payslip.workDays?.paid || 0} / ${payslip.workDays?.total || 0}</td>
                  <td class="text-right">₹${formatCurrency(payslip.grossSalary)}</td>
                  <td class="text-right">₹${formatCurrency(payslip.totalDeductions)}</td>
                  <td class="text-right">₹${formatCurrency(payslip.netSalary)}</td>
                  <td class="text-center">
                    <span class="status-chip status-${(payslip.status || '').toLowerCase()}">
                      ${(payslip.status || '').toUpperCase()}
                    </span>
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div style="margin-top: 30px; text-align: center;">
            <p><strong>Total Records:</strong> ${payslips.length}</p>
            <p style="color: #666; font-size: 12px;">This is a computer generated report</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handlePrintSingle = () => {
    if (!selectedPayslip) return;
    
    const printWindow = window.open('', '_blank');
    const printStyles = `
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .payslip-info { margin-bottom: 20px; }
        .info-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .info-section { margin-bottom: 20px; }
        .salary-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        .salary-table th, .salary-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        .salary-table th { background-color: #f5f5f5; }
        .salary-table .text-right { text-align: right; }
        .total-row { font-weight: bold; background-color: #f9f9f9; }
        .section-title { margin-bottom: 10px; color: #333; }
        @media print { body { margin: 10px; } }
      </style>
    `;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payslip - ${selectedPayslip.name}</title>
          ${printStyles}
        </head>
        <body>
          <div class="header">
            <h2>PAYSLIP</h2>
            <p>${formatDate(selectedPayslip.fromDate)} - ${formatDate(selectedPayslip.toDate)}</p>
          </div>
          
          <div class="payslip-info">
            <div class="info-row">
              <span><strong>Employee:</strong> ${selectedPayslip.employeeData?.empName || selectedPayslip.employeeDetails?.staff_name || 'N/A'}</span>
              <span><strong>Employee ID:</strong> ${selectedPayslip.employeeDetails?.emp_id || 'N/A'}</span>
            </div>
            <div class="info-row">
              <span><strong>Payslip No:</strong> ${selectedPayslip.name}</span>
              <span><strong>Status:</strong> ${(selectedPayslip.status || '').toUpperCase()}</span>
            </div>
          </div>
          
          <div class="info-section">
            <h3 class="section-title">Earnings</h3>
            <table class="salary-table">
              ${selectedPayslip.earnings?.map(item => `
                <tr>
                  <td>${item.earningsType}</td>
                  <td class="text-right">${formatCurrency(item.amount)}</td>
                </tr>
              `).join('') || ''}
              <tr class="total-row">
                <th>Gross Earnings</th>
                <th class="text-right">${formatCurrency(selectedPayslip.grossSalary)}</th>
              </tr>
            </table>
          </div>
          
          <div class="info-section">
            <h3 class="section-title">Deductions</h3>
            <table class="salary-table">
              ${selectedPayslip.deductions?.map(item => `
                <tr>
                  <td>${item.deductionType}</td>
                  <td class="text-right">${formatCurrency(item.amount)}</td>
                </tr>
              `).join('') || ''}
              <tr class="total-row">
                <th>Total Deductions</th>
                <th class="text-right">${formatCurrency(selectedPayslip.totalDeductions)}</th>
              </tr>
            </table>
          </div>
          
          <div class="info-section">
            <h3 class="section-title">Net Salary</h3>
            <table class="salary-table">
              <tr class="total-row">
                <th>Net Salary</th>
                <th class="text-right">${formatCurrency(selectedPayslip.netSalary)}</th>
              </tr>
            </table>
          </div>
          
          <div style="margin-top: 40px; text-align: center;">
            <p style="color: #666;">This is a computer generated payslip</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handlePrintRow = (payslip) => {
    // Create a temporary print-friendly version of the single payslip
    const printWindow = window.open('', '_blank');
    const printStyles = `
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .payslip-info { margin-bottom: 20px; }
        .info-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .salary-section { margin-top: 20px; }
        .salary-table { width: 100%; border-collapse: collapse; }
        .salary-table th, .salary-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        .salary-table th { background-color: #f5f5f5; }
        .total-row { font-weight: bold; background-color: #f9f9f9; }
        @media print { body { margin: 10px; } }
      </style>
    `;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payslip - ${payslip.name}</title>
          ${printStyles}
        </head>
        <body>
          <div class="header">
            <h2>PAYSLIP</h2>
            <p>${formatDate(payslip.fromDate)} - ${formatDate(payslip.toDate)}</p>
          </div>
          
          <div class="payslip-info">
            <div class="info-row">
              <span><strong>Employee:</strong> ${payslip.employeeDetails?.staff_name || 'N/A'}</span>
              <span><strong>Employee ID:</strong> ${payslip.employeeDetails?.emp_id || 'N/A'}</span>
            </div>
            <div class="info-row">
              <span><strong>Payslip No:</strong> ${payslip.name}</span>
              <span><strong>Status:</strong> ${payslip.status || 'N/A'}</span>
            </div>
          </div>
          
          <div class="salary-section">
            <h3>Earnings</h3>
            <table class="salary-table">
              <tr>
                <th>Basic Salary</th>
                <td align="right">₹${(payslip.earnings?.basic || 0).toLocaleString()}</td>
              </tr>
              <tr>
                <th>DA</th>
                <td align="right">₹${(payslip.earnings?.da || 0).toLocaleString()}</td>
              </tr>
              <tr>
                <th>HRA</th>
                <td align="right">₹${(payslip.earnings?.hra || 0).toLocaleString()}</td>
              </tr>
              <tr class="total-row">
                <th>Gross Earnings</th>
                <th align="right">₹${(payslip.grossSalary || 0).toLocaleString()}</th>
              </tr>
            </table>
            
            <h3 style="margin-top: 20px;">Deductions</h3>
            <table class="salary-table">
              <tr>
                <th>PF</th>
                <td align="right">₹${(payslip.deductions?.pf || 0).toLocaleString()}</td>
              </tr>
              <tr>
                <th>ESI</th>
                <td align="right">₹${(payslip.deductions?.esi || 0).toLocaleString()}</td>
              </tr>
              <tr>
                <th>Professional Tax</th>
                <td align="right">₹${(payslip.deductions?.pt || 0).toLocaleString()}</td>
              </tr>
              <tr class="total-row">
                <th>Total Deductions</th>
                <th align="right">₹${(payslip.totalDeductions || 0).toLocaleString()}</th>
              </tr>
            </table>
            
            <h3 style="margin-top: 20px;">Net Salary</h3>
            <table class="salary-table">
              <tr class="total-row">
                <th>Net Salary</th>
                <th align="right">₹${(payslip.netSalary || 0).toLocaleString()}</th>
              </tr>
            </table>
          </div>
          
          <div style="margin-top: 40px; text-align: center;">
            <p>This is a computer generated payslip</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Memoized image component to prevent re-renders (same as PayrollLeave)
  const EmployeeImage = useMemo(() => {
    // Inline SVG placeholder (40x40 circle) eliminates network 404 loops
    const FALLBACK_DATA_URL =
      'data:image/svg+xml;utf8,' +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
          <rect width="40" height="40" fill="#f0f0f0"/>
          <circle cx="20" cy="15" r="8" fill="#c0c0c0"/>
          <rect x="10" y="26" width="20" height="8" rx="4" fill="#d0d0d0"/>
        </svg>`
      );

    return ({ src, alt }) => (
      <img
        src={src || FALLBACK_DATA_URL}
        alt={alt}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          objectFit: 'cover',
          border: '1px solid #e0e0e0'
        }}
        onError={(e) => {
          // Prevent infinite onError loop by removing handler, then set fallback
          e.currentTarget.onerror = null;
          e.currentTarget.src = FALLBACK_DATA_URL;
        }}
      />
    );
  }, []);

  // Fetch employee image by ID
  const fetchEmployeeImage = async (employeeId) => {
    if (!employeeId || employeeImages[employeeId]) return employeeImages[employeeId];
    
    try {
      console.log('Fetching employee image for ID:', employeeId);
      const response = await axios.post(`employee/getEmployeeById`, { id: employeeId }, HEADERS);
      console.log('API response:', response.data);
      const employee = response.data?.data || response.data;
      if (employee) {
        const imageUrl = 
          employee.imageUrl ||
          employee.profileImage ||
          employee.image ||
          employee.employeePhoto ||
          employee.empImage ||
          null;
        
        console.log('Found image URL:', imageUrl);
        if (imageUrl) {
          setEmployeeImages(prev => ({ ...prev, [employeeId]: imageUrl }));
          return imageUrl;
        }
      }
    } catch (error) {
      console.warn('Failed to fetch employee image for ID:', employeeId, error);
    }
    return null;
  };

  useEffect(() => {
    fetchPayslips();
  }, [page, rowsPerPage]);

  const fetchPayslips = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (filters.fromDate) {
        params.fromDate = filters.fromDate.format("YYYY-MM-DD");
      }
      if (filters.toDate) {
        params.toDate = filters.toDate.format("YYYY-MM-DD");
      }
      if (filters.status) {
        params.status = filters.status;
      }

      const response = await axios.get("/payroll/paySlip", { params }).catch(() => ({ 
        data: { list: [], totalCount: 0 } 
      }));

      if (response.data) {
        const payslipsWithImages = await Promise.all((response.data.list || []).map(async (payslip, idx) => {
          // Get employee ID from payslip
          const employeeId = payslip?.employeeId || payslip?.employee?._id || payslip?.employeeDetails?._id;
          let imageUrl = null;
          
          if (employeeId) {
            imageUrl = await fetchEmployeeImage(employeeId);
          }
          
          // Build full URL if we have a relative path
          if (imageUrl && !imageUrl.startsWith('http')) {
            const base = (axios.defaults.baseURL || '').replace(/\\\\/g, '/').replace(/\/$/, '');
            const path = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
            imageUrl = `${base}${path}`;
          }
          
          if (idx === 0) {
            console.log(
              "PayslipList - Employee ID:", employeeId,
              "Fetched image URL:", imageUrl,
              "Base URL:", axios.defaults.baseURL
            );
          }
          
          return {
            ...payslip,
            employeeImage: imageUrl
          };
        }));
        
        setPayslips(payslipsWithImages);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (err) {
      console.error("Error fetching payslips:", err);
      setPayslips([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleViewPayslip = async (payslipId) => {
    try {
      const response = await axios.get(`/payroll/paySlip/${payslipId}`).catch(() => ({ data: null }));
      if (response.data) {
        // Backend returns an array with one element, extract it
        const payslipData = Array.isArray(response.data) ? response.data[0] : response.data;
        console.log("📋 Payslip details loaded:", payslipData);
        setSelectedPayslip(payslipData);
        setViewDialogOpen(true);
      }
    } catch (err) {
      console.error("Error fetching payslip details:", err);
      alert("Unable to fetch payslip details. Backend API not available.");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchPayslips();
    setFilterDialogOpen(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return dayjs(date).format("DD MMM YYYY");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "DRAFT":
        return "default";
      case "POSTED":
        return "success";
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">Payslip List</Typography>
          <Box>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrintList}
              sx={{ mr: 2 }}
            >
              Print List
            </Button>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={() => setFilterDialogOpen(true)}
            >
              Filters
            </Button>
          </Box>
        </Box>

        <Card>
          <CardContent>
            <div id="payslip-list-printable">
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Payslip No.</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Work Days</TableCell>
                    <TableCell align="right">Gross Salary</TableCell>
                    <TableCell align="right">Deductions</TableCell>
                    <TableCell align="right">Net Salary</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : payslips.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        No payslips found
                      </TableCell>
                    </TableRow>
                  ) : (
                    payslips.map((payslip) => (
                      <TableRow key={payslip._id}>
                        <TableCell>
                          <EmployeeImage 
                            src={payslip.employeeImage} 
                            alt={payslip.employeeDetails?.staff_name || 'Employee'} 
                          />
                        </TableCell>
                        <TableCell>{payslip.name}</TableCell>
                        <TableCell>
                          {payslip.employeeDetails?.staff_name || "N/A"}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {payslip.employeeDetails?.emp_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatDate(payslip.fromDate)} - {formatDate(payslip.toDate)}
                        </TableCell>
                        <TableCell>
                          {payslip.workDays?.paid || 0} / {payslip.workDays?.total || 0}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(payslip.grossSalary)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(payslip.totalDeductions)}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {formatCurrency(payslip.netSalary)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payslip.status}
                            size="small"
                            color={getStatusColor(payslip.status)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleViewPayslip(payslip._id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton 
                            size="small"
                            onClick={() => handlePrintRow(payslip)}
                            title="Print Payslip"
                          >
                            <PrintIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            </div>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </CardContent>
        </Card>

        {/* Filter Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Filter Payslips</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="From Date"
                  value={filters.fromDate}
                  onChange={(newValue) =>
                    setFilters({ ...filters, fromDate: newValue })
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="To Date"
                  value={filters.toDate}
                  onChange={(newValue) =>
                    setFilters({ ...filters, toDate: newValue })
                  }
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  label="Status"
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value="">All</option>
                  <option value="DRAFT">Draft</option>
                  <option value="POSTED">Posted</option>
                  <option value="CANCELLED">Cancelled</option>
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleApplyFilters} variant="contained">
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Payslip Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Payslip Details</DialogTitle>
          <DialogContent>
            <div id="payslip-single-printable">
            {selectedPayslip && (
              <Box>
                {/* Employee Info */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Employee
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedPayslip.employeeData?.empName || selectedPayslip.employeeDetails?.staff_name || "N/A"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Payslip Number
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedPayslip.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Period
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedPayslip.fromDate)} -{" "}
                      {formatDate(selectedPayslip.toDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Work Days
                    </Typography>
                    <Typography variant="body1">
                      {selectedPayslip.workDays?.paid} /{" "}
                      {selectedPayslip.workDays?.total}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Salary Breakdown */}
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Component</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedPayslip.salaryCalc
                        ?.filter((item) => item.isEarning)
                        .map((item, index) => (
                          <TableRow key={`earning-${index}`}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(item.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Gross Salary</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="success.main">
                            {formatCurrency(selectedPayslip.grossSalary)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Divider />
                        </TableCell>
                      </TableRow>
                      {selectedPayslip.salaryCalc
                        ?.filter((item) => !item.isEarning)
                        .map((item, index) => (
                          <TableRow key={`deduction-${index}`}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell align="right">
                              {formatCurrency(item.amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      <TableRow>
                        <TableCell>
                          <Typography fontWeight="bold">Total Deductions</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color="error.main">
                            {formatCurrency(selectedPayslip.totalDeductions)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={2}>
                          <Divider />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <Typography variant="h6">Net Salary</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" color="primary">
                            {formatCurrency(selectedPayslip.netSalary)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button 
              variant="contained" 
              startIcon={<PrintIcon />}
              onClick={handlePrintSingle}
            >
              Print
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default PayslipList;
